import { dateToHourAndMinutes, dateToShortMonth, formatToShortDate, isToday } from '@helpers/date';
import type {
  AxfoodDigitalReceiptElementViewModel,
  AxfoodExternalVoucher,
  AxfoodOrderElementViewModel,
} from '@occ/api-client';
import type { useTranslations } from 'next-intl';

type TFunction = ReturnType<typeof useTranslations<'personalElements'>>;
import { discountTypes } from '@molecules/PersonalElementsCards/PersonalElementsVoucher/PersonalElementsVoucher';
import { CardDataType } from '@molecules/PersonalElementsCards/PersonalElementsOrder/PersonalElementsOrder';
import getVoucherType, { VoucherTypes } from './getVoucherType';

export const handleOrderData = (data: AxfoodOrderElementViewModel, t: TFunction): CardDataType => {
  const { nextDay, deliveryType, slotStartDate = 0, slotEndDate } = data;
  // Set title
  const title = (t as any)(`order.status.${data.titleDisplayKey}`);
  // Set text
  const time = `${dateToHourAndMinutes(slotStartDate)}-${dateToHourAndMinutes(slotEndDate)}`;
  const date = nextDay
    ? `${t('order.tomorrow')} ${time}`
    : isToday(slotStartDate)
      ? `${t('order.today')} ${time}`
      : `${dateToShortMonth(slotStartDate)} ${time}`;
  const text = `${(t as any)(`order.${deliveryType?.code}`)} ${date}`;

  // Set button
  const button = t('order.button.show');

  return { title, text, button };
};

export const handleReceiptData = (data: AxfoodDigitalReceiptElementViewModel, t: TFunction): CardDataType => {
  return {
    title: t('digitalReceipt.title'),
    text: t('digitalReceipt.text', {
      mode: data.digitalReceiptAvailable ? t('digitalReceipt.inStore') : t('digitalReceipt.online'),
      date: data.bookingDate ? formatToShortDate(data.bookingDate) : '',
    }),
    button: t('digitalReceipt.button'),
  };
};

export const handleVoucherData = (voucher: AxfoodExternalVoucher, t: TFunction) => {
  const unit = voucher.discountType === discountTypes.ABSOLUTE ? 'kr' : '%';
  const title = `${t('voucher.title')}:${voucher.discountValue}${unit}`;
  const minPurchase =
    voucher.minPurchaseAmount && voucher.minPurchaseAmount > 0
      ? `${t('voucher.minPurchaseAmount', { amount: voucher.minPurchaseAmount })} `
      : '';
  const voucherType = getVoucherType(voucher);
  const channel = voucherType === VoucherTypes.OMNI ? '' : ` ${(t as any)(`voucher.channel.${voucherType}`)}.`;
  const noWrapDate = voucher.expirationDate?.replace(/-/g, '\u2011') ?? '';
  const text = `${minPurchase}${t('voucher.text')} ${noWrapDate}.${channel}`;
  const button = t('voucher.button');

  return { title, text, button };
};
